import React, { useEffect, useState, useRef, useMemo } from "react";
import * as Styled from "./style";
import { HoverModal } from "./HoverModalStyles";
import { FaChevronRight } from "react-icons/fa6";
import { FaChevronLeft, FaPlay } from "react-icons/fa";
import useDetailsHook from "../../hooks/DetailsHook";
import { trackUserClick } from "../../services/Analytics";
import { useConfig } from "../../ConfigContext"; // Adjust path as needed

const ImageWithFallback = ({ item, onClick }) => {
  const [isError, setIsError] = useState(false);
  const imageUrl = item.thumbnail_image || item.portrait || item.landscape;

  return (
    <div onClick={onClick} className="imageContainer">
      {!isError ? (
        <img
          src={imageUrl}
          alt={item.name || item.title}
          className="imagebox"
          onError={() => setIsError(true)}
        />
      ) : (
        <div className="imagebox" style={{ backgroundColor: "#B87333" }} />
      )}
    </div>
  );
};

const Standard = ({ sectionData, detailspage, detailList, handleDetails }) => {
  const { handlePlayRoute } = useDetailsHook();
  const config = useConfig();
  const enableLogin = config?.enable_login;

  const normalizedSectionData = useMemo(() => {
    return Array.isArray(sectionData)
      ? sectionData
      : sectionData?.related_content
        ? [{ type: "gallery", title: "More Like This", items: sectionData.related_content }]
        : [];
  }, [sectionData]);

  const [galleryRows, setGalleryRows] = useState([]);
  const [scrollPositions, setScrollPositions] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const [isHoveringModal, setIsHoveringModal] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const hoverTargetRef = useRef(null);
  const rowRefs = useRef([]);
  const detailListRef = useRef([]);

  useEffect(() => {
    setGalleryRows(
      normalizedSectionData.filter((item) => item?.type?.toLowerCase() === "gallery")
    );
  }, [normalizedSectionData]);

  const updateScrollPosition = (index, isDetailList = false) => {
    const container = isDetailList ? detailListRef.current : rowRefs.current[index];
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setScrollPositions((prev) => ({
        ...prev,
        [index]: {
          scrollLeft,
          canScrollRight: scrollLeft + clientWidth < scrollWidth,
          canScrollLeft: scrollLeft > 0,
        },
      }));
    }

    if (hoveredItem && hoverTargetRef.current) {
      const rect = hoverTargetRef.current.getBoundingClientRect();
      setHoverPosition({
        left: rect.left + rect.width / 2,
        top: rect.top + rect.height / 2,
      });
    }
  };

  const moveRight = (index, isDetailList = false) => {
    const container = isDetailList ? detailListRef.current : rowRefs.current[index];
    if (container) {
      container.scrollBy({ left: window.innerWidth, behavior: "smooth" });
      setTimeout(() => updateScrollPosition(index, isDetailList), 300);
    }
  };

  const moveLeft = (index, isDetailList = false) => {
    const container = isDetailList ? detailListRef.current : rowRefs.current[index];
    if (container) {
      container.scrollBy({ left: -window.innerWidth, behavior: "smooth" });
      setTimeout(() => updateScrollPosition(index, isDetailList), 300);
    }
  };

  const handleRowLeave = (index, isDetailList = false) => {
    setScrollPositions((prev) => {
      const key = index;
      if (!prev[key]) return prev;
      return {
        ...prev,
        [key]: {
          ...prev[key],
          canScrollLeft: false,
          canScrollRight: false,
        },
      };
    });
  };

  const handleItemClick = (item) => {
    trackUserClick({
      source: "DETAILS_RELATED",
      action: "OPEN_DETAILS",
      contentId: item.id,
      slug: item.slug,
      contentType: item.content_type,
      title: item.name || item.title,
    });

    const typeSlug = item.content_type
      ? item.content_type.toLowerCase().replace(/\s+/g, "-")
      : "creators"; 
    handleDetails(item.slug || item.id, typeSlug);
  };

  const handlePlayVideo = (item) => {
    trackUserClick({
      source: "DETAILS_RELATED",
      action: "PLAY",
      contentId: item.id,
      slug: item.slug,
      contentType: item.content_type,
      title: item.name || item.title,
      position: item.progress_seconds || 0,
    });

    handlePlayRoute({
      slug: item.slug || item.id,
      content_type: item.content_type,
      type: item.type,
      position: item.progress_seconds || 0,
    });
  };
  
  const handleCardHover = (item, event) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    hoverTargetRef.current = event.currentTarget;

    hoverTimeoutRef.current = setTimeout(() => {
      if (!hoverTargetRef.current) return;
      const rect = hoverTargetRef.current.getBoundingClientRect();
      setHoveredItem(item);
      setHoverPosition({
        left: rect.left + rect.width / 2,
        top: rect.top + rect.height / 2,
      });
    }, 500);
  };

  const handleCardLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringModal) {
        setHoveredItem(null);
        setHoverPosition(null);
        hoverTargetRef.current = null;
      }
    }, 100);
  };

  const handleModalEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHoveringModal(true);
  };

  const handleModalLeave = () => {
    setIsHoveringModal(false);
    setHoveredItem(null);
    setHoverPosition(null);
    hoverTargetRef.current = null;
  };

  useEffect(() => {
    if (!hoveredItem || !hoverTargetRef.current) return;

    const handleWindowScroll = () => {
      if (!hoverTargetRef.current) return;
      const rect = hoverTargetRef.current.getBoundingClientRect();
      setHoverPosition({
        left: rect.left + rect.width / 2,
        top: rect.top + rect.height / 2,
      });
    };

    window.addEventListener("scroll", handleWindowScroll);
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, [hoveredItem]);

  return (
    <div>
      {detailspage ? (
        <>
          {detailList && (
            <Styled.StandardBox>
              <Styled.HeadingBox className="heading">More like this</Styled.HeadingBox>
              <Styled.MainContainer
                onMouseEnter={() => updateScrollPosition(0, true)}
                onMouseLeave={() => handleRowLeave(0, true)}
              >
                {scrollPositions[0]?.canScrollLeft && (
                  <Styled.ButtonLeft onClick={() => moveLeft(0, true)}>
                    <FaChevronLeft />
                  </Styled.ButtonLeft>
                )}
                {scrollPositions[0]?.canScrollRight && (
                  <Styled.ButtonRight onClick={() => moveRight(0, true)}>
                    <FaChevronRight />
                  </Styled.ButtonRight>
                )}

                <div
                  ref={detailListRef}
                  onScroll={() => updateScrollPosition(0, true)}
                  className="rowsStyle"
                >
                  {detailList.map((item, index) => (
                    <div
                      key={index}
                      className="cardbox"
                      onMouseEnter={(e) => handleCardHover(item, e)}
                      onMouseLeave={handleCardLeave}
                    >
                      <ImageWithFallback
                        item={item}
                        onClick={() => handleItemClick(item)}
                      />
                    </div>
                  ))}
                </div>
              </Styled.MainContainer>
            </Styled.StandardBox>
          )}
        </>
      ) : (
        <>
          {galleryRows.map((row, rowIndex) => (
            <Styled.StandardBox key={rowIndex}>
              <Styled.HeadingBox className="heading">{row.title}</Styled.HeadingBox>
              <Styled.MainContainer
                onMouseEnter={() => updateScrollPosition(rowIndex)}
                onMouseLeave={() => handleRowLeave(rowIndex)}
              >
                {scrollPositions[rowIndex]?.canScrollLeft && (
                  <Styled.ButtonLeft onClick={() => moveLeft(rowIndex)}>
                    <FaChevronLeft />
                  </Styled.ButtonLeft>
                )}
                {scrollPositions[rowIndex]?.canScrollRight && (
                  <Styled.ButtonRight onClick={() => moveRight(rowIndex)}>
                    <FaChevronRight />
                  </Styled.ButtonRight>
                )}

                <div
                  ref={(el) => (rowRefs.current[rowIndex] = el)}
                  onScroll={() => updateScrollPosition(rowIndex)}
                  className="rowsStyle"
                >
                  {row.items.map((item, index) => (
                    <div
                      key={index}
                      className="cardbox"
                      onMouseEnter={(e) => handleCardHover(item, e)}
                      onMouseLeave={handleCardLeave}
                    >
                      <ImageWithFallback
                        item={item}
                        onClick={() => handleItemClick(item)}
                      />
                    </div>
                  ))}
                </div>
              </Styled.MainContainer>
            </Styled.StandardBox>
          ))}
        </>
      )}

      {hoveredItem && hoverPosition && (
        <HoverModal
          left={hoverPosition.left}
          top={hoverPosition.top}
          onMouseEnter={handleModalEnter}
          onMouseLeave={handleModalLeave}
        >
          <div className="hover-modal-content" onClick={() => handleItemClick(hoveredItem)}>
            <div className="hover-modal-image-wrapper">
              <img
                src={
                  hoveredItem.thumbnail_image ||
                  hoveredItem.portrait ||
                  hoveredItem.landscape
                }
                alt={hoveredItem.name || hoveredItem.title || "Preview"}
                className="hover-modal-image"
              />
            </div>
            <div className="hover-modal-body">
              {enableLogin === false ? (
                <button
                  className="hover-modal-play-button"
                  onClick={(e) => {handlePlayVideo(hoveredItem); e.stopPropagation()}}
                >
                  <FaPlay />
                  <span>Watch Now</span>
                </button>
              ) : (
                <button
                  className="hover-modal-play-button"
                  onClick={(e) => {handlePlayVideo(hoveredItem); e.stopPropagation()}}
                >
                  <FaPlay />
                  <span>Watch Now</span>
                </button>
              )}
              <div className="hover-modal-title">
                {hoveredItem.name || hoveredItem.title || "Untitled"}
              </div>
              {hoveredItem.description && (
                <div className="hover-modal-description">
                  {hoveredItem.description.length > 200
                    ? `${hoveredItem.description.slice(0, 197)}...`
                    : hoveredItem.description}
                </div>
              )}
            </div>
          </div>
        </HoverModal>
      )}
    </div>
  );
};

export default Standard;